/**
 * Phone number utilities for Pontis Life.
 * See docs/phone-auth-spec.md section 3 for format rules.
 *
 * All phone numbers are stored in E.164 format: +15551234567
 */

/**
 * Detects whether a string looks like a phone number vs an email.
 * Contains @ → email. Starts with + or digit, no @ → phone.
 */
export function isPhoneNumber(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) return false;
  return /^\+?\d/.test(trimmed) && !trimmed.includes('@');
}

/**
 * Normalizes a phone number to E.164 format.
 * Assumes US numbers if no country code prefix.
 *
 * Examples:
 *   "(555) 123-4567"  → "+15551234567"
 *   "555-123-4567"    → "+15551234567"
 *   "5551234567"      → "+15551234567"
 *   "15551234567"     → "+15551234567"
 *   "+15551234567"    → "+15551234567"
 *   "+44 20 7946 0958" → "+442079460958"
 */
export function normalizePhone(input: string): string {
  const trimmed = input.trim();

  // Preserve leading + if present
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (hasPlus) {
    return `+${digits}`;
  }

  // 10 digits → assume US, prepend +1
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // 11 digits starting with 1 → assume US with country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  // Otherwise prepend +
  return `+${digits}`;
}

/**
 * Formats an E.164 phone number for display.
 * US numbers: +15551234567 → (555) 123-4567
 * Non-US: returned as-is.
 */
export function formatPhoneForDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  // US number: 11 digits starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const line = digits.slice(7);
    return `(${area}) ${prefix}-${line}`;
  }

  return phone;
}
